import { Link } from "react-router-dom";
import TerminalAnimation from "../components/TerminalAnimation";
import { Zap, Lock, Globe, Activity, ArrowRight, Github, KeyRound, Layers } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PublicLayout from "../components/PublicLayout";
import { useEffect } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const FEATURES = [
    { icon: Activity, title: "PERSISTENT SUBDOMAINS", body: "Your URL never changes. Reserve project.synapselink.cloud once — it stays active forever, on every plan, even free." },
    { icon: Layers, title: "REQUEST INSPECTOR", body: "Live, scrolling stream of every HTTP hit: full headers, JSON body, response codes. Replay webhooks with one click." },
    { icon: Lock, title: "PASSWORD PROTECT", body: "Toggle basic auth on any tunnel from the dashboard. No restart, no flag rewriting. Lock the door instantly." },
    { icon: Globe, title: "BRING YOUR DOMAIN", body: "CNAME api.yourcompany.com → synapselink. SSL handled. Custom domains for the Pro tier." },
    { icon: KeyRound, title: "CLI · API KEYS", body: "Authenticate via long-lived keys. Rotate them, revoke them, scope them. No browser pop-ups in CI." },
    { icon: Zap, title: "RATE-LIMITED RELAY", body: "Edge nodes throttle abuse before it ever touches your laptop. Sleep through the DDoS." },
];

const PRICING = [
    { name: "FREE", price: "$0", desc: "Solo hackers", features: ["1 reserved subdomain", "Live request inspector", "Password protect", "Community support"], cta: "START FREE" },
    { name: "PRO", price: "$12", desc: "Per developer / month", features: ["Unlimited subdomains", "Custom domains + SSL", "Replay & export logs", "Priority relay nodes", "Email support"], cta: "GO PRO", featured: true },
    { name: "TEAM", price: "$48", desc: "5 seats included", features: ["Team-shared subdomains", "Org-level API keys", "SAML SSO", "Audit logs", "SLA support"], cta: "BOOK CALL" },
];

export default function Landing() {
    const { user } = useAuth();

    useEffect(() => {
        document.title = "SynapseLink - Best Free Ngrok Alternative & Secure Localhost Tunneling";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute("content", "Expose localhost securely. SynapseLink is a developer-first ngrok alternative with persistent subdomains, a live request inspector, and secure reverse proxy controls.");
        }
    }, []);

    return (
        <PublicLayout>
            {/* HERO */}
            <section className="relative border-b border-[#27272A] overflow-hidden">
                <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1741795990534-9e5050558172?crop=entropy&cs=srgb&fm=jpg&q=85')", backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "screen" }} />
                <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 px-6 py-16 lg:py-24 relative">
                    <div className="lg:col-span-6 flex flex-col justify-center">
                        <div className="label-mono text-[#00FF41] mb-4">// SECURE REVERSE PROXY & LOCALHOST TUNNELING</div>
                        <h1 className="heading-xl mb-6">
                            EXPOSE LOCALHOST.<br />
                            SECURE TUNNEL.<br />
                            <span className="text-[#00FF41]">WITHOUT NGROK.</span>
                        </h1>
                        <p className="text-[#A1A1AA] text-base sm:text-lg max-w-xl mb-8 leading-relaxed">
                            SynapseLink is a developer-first <strong>ngrok alternative</strong>. Set up a <strong>secure reverse proxy</strong> to <strong>expose localhost</strong>, run <strong>localhost tunneling</strong> streams, and access a premium <strong>webhook testing tool</strong> with persistent subdomains.
                        </p>
                        <div className="flex flex-wrap items-center gap-4 mb-10">
                            <Link to="/register" className="tf-btn" data-testid="hero-cta-register">
                                CLAIM YOUR SUBDOMAIN <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/how-it-works" className="text-xs uppercase tracking-widest text-[#A1A1AA] hover:text-[#FAFAFA] flex items-center gap-2" data-testid="hero-cta-howitworks">
                                <Github className="w-4 h-4" /> How it works
                            </Link>
                        </div>
                        <div className="grid grid-cols-3 gap-0 border border-[#27272A]">
                            {[
                                ["12K+", "Tunnels Live"],
                                ["41M", "Requests Today"],
                                ["99.99%", "Relay Uptime"],
                            ].map(([n, l], i) => (
                                <div key={i} className="px-4 py-4 border-r last:border-r-0 border-[#27272A]">
                                    <div className="font-display text-2xl font-bold">{n}</div>
                                    <div className="label-mono mt-1">{l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-6 flex items-center">
                        <div className="w-full">
                            <TerminalAnimation />
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="features" className="border-b border-[#27272A]">
                <div className="max-w-[1400px] mx-auto px-6 py-20">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-5">
                            <div className="label-mono text-[#00FF41] mb-3">// 02 — FEATURES</div>
                            <h2 className="heading-lg">BUILT FOR THE DEV WHO LIVES IN THE TERMINAL.</h2>
                        </div>
                        <p className="lg:col-span-7 text-[#A1A1AA] text-base sm:text-lg leading-relaxed self-end">
                            Every feature exists because something else broke our flow. Lost subdomains. Webhook 500s with zero context. Restarting tunnels just to add basic auth. We fixed each of them.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#27272A]">
                        {FEATURES.map((f, i) => (
                            <div key={i} className="p-7 border-r border-b border-[#27272A] last:border-r-0 [&:nth-child(3n)]:border-r-0 hover:bg-[#0A0A0A] transition-colors group" data-testid={`feature-card-${i}`}>
                                <f.icon className="w-7 h-7 text-[#00FF41] mb-5" strokeWidth={1.75} />
                                <h3 className="font-display text-lg font-bold mb-3 tracking-tight">{f.title}</h3>
                                <p className="text-sm text-[#A1A1AA] leading-relaxed">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="border-b border-[#27272A]">
                <div className="max-w-[1400px] mx-auto px-6 py-20">
                    <div className="label-mono text-[#00FF41] mb-3">// 03 — HOW IT WORKS</div>
                    <h2 className="heading-lg mb-12">THREE STEPS. ZERO YAML.</h2>
                    <div className="grid lg:grid-cols-3 gap-0 border border-[#27272A]">
                        {[
                            ["01", "INSTALL", "npm install -g @synapselink/cli (or use the Windows bootstrap script) to install synapselink globally."],
                            ["02", "AUTHENTICATE", "synapselink login uses an API key from your dashboard. Long-lived, scoped, revocable. CI-friendly."],
                            ["03", "EXPOSE", "synapselink 3000 instantly forwards your local server. Inspector spins up. URL is yours forever."],
                        ].map(([n, t, b], i) => (
                            <div key={i} className="p-8 border-r last:border-r-0 border-[#27272A]">
                                <div className="font-display text-6xl font-bold text-[#00FF41] mb-4">{n}</div>
                                <div className="font-display text-xl uppercase font-bold mb-2 tracking-tight">{t}</div>
                                <p className="text-sm text-[#A1A1AA] leading-relaxed">{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="border-b border-[#27272A]">
                <div className="max-w-[1400px] mx-auto px-6 py-20">
                    <div className="label-mono text-[#00FF41] mb-3">// 04 — PRICING</div>
                    <h2 className="heading-lg mb-12">PAY FOR THE TUNNEL, NOT THE THEATER.</h2>
                    <div className="grid lg:grid-cols-3 gap-0 border border-[#27272A]">
                        {PRICING.map((p, i) => (
                            <div key={i} className={`p-8 border-r last:border-r-0 border-[#27272A] flex flex-col ${p.featured ? "bg-[#0A0A0A] border-t-2 border-t-[#00FF41]" : ""}`} data-testid={`pricing-card-${p.name.toLowerCase()}`}>
                                <div className="label-mono">{p.desc}</div>
                                <div className="font-display text-3xl font-bold mt-1 mb-1">{p.name}</div>
                                <div className="flex items-baseline gap-1 mb-6">
                                    <span className="font-display text-5xl font-bold">{p.price}</span>
                                    <span className="text-sm text-[#52525B]">/mo</span>
                                </div>
                                <ul className="space-y-2 mb-8 text-sm text-[#A1A1AA]">
                                    {p.features.map((f, j) => (
                                        <li key={j} className="flex items-start gap-2">
                                            <span className="text-[#00FF41] mt-0.5">▸</span> {f}
                                        </li>
                                    ))}
                                </ul>
                                <Link to="/pricing" className={p.featured ? "tf-btn justify-center" : "tf-btn tf-btn-ghost justify-center"} data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                                    {p.cta} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ SECTION (AEO, GEO & SEO TARGETED) */}
            <section id="faq" className="border-b border-[#27272A] bg-[#070707] py-20">
                <div className="max-w-[800px] mx-auto px-6">
                    <div className="label-mono text-[#00FF41] mb-3 text-center">// 05 — COMMON QUESTIONS</div>
                    <h2 className="heading-md text-center mb-10 text-[#FAFAFA]">FAQ: Localhost Tunneling & Alternatives</h2>
                    <Accordion type="single" collapsible className="w-full border border-[#27272A] bg-[#0A0A0A] divide-y divide-[#27272A]">
                        <AccordionItem value="item-1" className="border-b-0 px-6 py-2">
                            <AccordionTrigger className="font-display text-sm tracking-wide text-[#FAFAFA] hover:text-[#00FF41] uppercase py-4">
                                What is the best free ngrok alternative for developers?
                            </AccordionTrigger>
                            <AccordionContent className="text-xs text-[#A1A1AA] leading-relaxed pb-4">
                                SynapseLink is a premier <strong>free ngrok alternative for developers</strong> who are tired of random, rotating subdomains.
                                Our free tier includes a permanent, reserved subdomain so you don't have to reconfigure third-party callbacks
                                and stripe configurations every time you restart your tunneling proxy client.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-2" className="border-b-0 px-6 py-2">
                            <AccordionTrigger className="font-display text-sm tracking-wide text-[#FAFAFA] hover:text-[#00FF41] uppercase py-4">
                                How to expose local server to internet securely?
                            </AccordionTrigger>
                            <AccordionContent className="text-xs text-[#A1A1AA] leading-relaxed pb-4">
                                To <strong>expose local server to internet securely</strong>, SynapseLink routes all connection traffic through TLS-terminated
                                edge gateway nodes. The CLI authenticates via scoped API keys rather than browser loops. You can enable
                                basic authentication password gates instantly from your web dashboard, protecting your local environment
                                from unauthorized public access and DDoS spikes.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-3" className="border-b-0 px-6 py-2">
                            <AccordionTrigger className="font-display text-sm tracking-wide text-[#FAFAFA] hover:text-[#00FF41] uppercase py-4">
                                How to test webhooks on localhost without ngrok?
                            </AccordionTrigger>
                            <AccordionContent className="text-xs text-[#A1A1AA] leading-relaxed pb-4">
                                If you want to <strong>test webhooks on localhost without ngrok</strong>, SynapseLink provides a persistent URL
                                combined with an interactive Webhook Request Inspector. When a webhook hits your tunnel, you can inspect
                                headers, path query variables, and the JSON payload in real time. If your local code fails, you can replay
                                the webhook payload with one click from the dashboard without triggering a new external event.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-4" className="border-b-0 px-6 py-2">
                            <AccordionTrigger className="font-display text-sm tracking-wide text-[#FAFAFA] hover:text-[#00FF41] uppercase py-4">
                                Is there an open source ngrok alternative for team collaboration?
                            </AccordionTrigger>
                            <AccordionContent className="text-xs text-[#A1A1AA] leading-relaxed pb-4">
                                SynapseLink is built with team collaboration in mind. Unlike other tools that charge high per-seat fees,
                                we provide a <strong>cheaper ngrok alternative for small teams</strong>. Features include team-shared subdomains,
                                organization-wide API keys, audit logs, and unified dashboard billing. The core multiplexer architecture
                                supports custom integration workflows.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-5" className="border-b-0 px-6 py-2">
                            <AccordionTrigger className="font-display text-sm tracking-wide text-[#FAFAFA] hover:text-[#00FF41] uppercase py-4">
                                How to get a public URL for localhost fast?
                            </AccordionTrigger>
                            <AccordionContent className="text-xs text-[#A1A1AA] leading-relaxed pb-4">
                                You can get a public URL for localhost in under 30 seconds: install our global client via
                                <code>npm install -g @synapselink/cli</code> (or run our Windows PowerShell bootstrap command),
                                log in with your dashboard token, and run <code>synapselink 3000</code>. A public SSL-encrypted
                                URL is instantly assigned and remains yours.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-6" className="border-b-0 px-6 py-2">
                            <AccordionTrigger className="font-display text-sm tracking-wide text-[#FAFAFA] hover:text-[#00FF41] uppercase py-4">
                                Can I host a self hosted secure tunnel alternative to ngrok?
                            </AccordionTrigger>
                            <AccordionContent className="text-xs text-[#A1A1AA] leading-relaxed pb-4">
                                Yes. SynapseLink supports custom proxy relays. Enterprise users can deploy the secure tunnel gateway
                                nodes on their private cloud VPC or server infrastructure, keeping all sensitive HTTP packets,
                                payload logs, and data streams within their corporate firewall boundaries.
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </section>

            {/* CTA */}
            <section className="border-b border-[#27272A]">
                <div className="max-w-[1400px] mx-auto px-6 py-24 text-center">
                    <div className="label-mono text-[#00FF41] mb-3 justify-center">// READY?</div>
                    <h2 className="heading-xl mb-4">BUILD WEBHOOKS<br />WITHOUT FEAR.<span className="text-[#00FF41]">_</span></h2>
                    <p className="text-[#A1A1AA] max-w-xl mx-auto mb-8">Sign up in 11 seconds. Reserve a subdomain you can put on a slide deck. Inspect every byte that flies through it.</p>
                    <Link to="/register" className="tf-btn inline-flex" data-testid="footer-cta-register">
                        CLAIM YOUR SUBDOMAIN <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
            {/* hgygygyku */}
        </PublicLayout>
    );
}